import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { 
  Home,
  Store, 
  Calendar, 
  ShoppingBag, 
  Users, 
  CalendarDays, 
  History, 
  Settings, 
  Briefcase, 
  Heart, 
  CalendarCheck,
  Wrench,
  BarChart2,
  Shield,
  Moon,
  Sun,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronUp,
  Menu,
  ShoppingCart,
  Tag,
  Package,
  FileText,
  Gift,
  FileSearch
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ui/theme-toggle";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  breakpoint?: 'mobile' | 'tablet' | 'desktop';
}

interface MenuItem {
  icon: React.ComponentType<any>;
  label: string;
  href: string;
  active?: boolean;
  badge?: string;
  disabled?: boolean;
}

interface MenuGroup {
  label: string;
  icon: React.ComponentType<any>;
  items: MenuItem[];
}

const mainMenuItems: MenuItem[] = [
  { icon: Home, label: "Início", href: "/app/home" },
  { icon: ShoppingBag, label: "Meus Produtos", href: "/app/meus-produtos" },
  { icon: Store, label: "Loja de Serviços", href: "/app/loja-de-servicos" },
  { icon: ShoppingCart, label: "Carrinho", href: "/app/carrinho" },
  { icon: Package, label: "Meus Pedidos", href: "/app/meus-pedidos" },
  { icon: Tag, label: "Cupons de Desconto", href: "/app/cupons", disabled: true },
  { icon: Calendar, label: "Consultas & Agendamentos", href: "/app/consultas", disabled: true },
  { icon: Users, label: "Comunidade", href: "/app/comunidade" },
];

const professionalMenu: MenuGroup = {
  label: "Menu Profissional",
  icon: Briefcase,
  items: [
    { icon: Heart, label: "Meus Clientes", href: "/app/prof/clientes" },
    { icon: CalendarCheck, label: "Minha Agenda Profissional", href: "/app/prof/agenda" },
    { icon: Wrench, label: "Meus Serviços", href: "/app/prof/servicos" },
    { icon: BarChart2, label: "Relatórios Profissionais", href: "/app/prof/relatorios" },
  ]
};

const adminMenu: MenuGroup = {
  label: "Menu Admin",
  icon: Shield,
  items: [
    { icon: Settings, label: "Gestão de Usuários", href: "/app/admin/usuarios" },
    { icon: Wrench, label: "Gestão de Serviços", href: "/app/admin/servicos" },
    { icon: FileText, label: "Gestão do Blog", href: "/app/admin/blog" },
    { icon: FileText, label: "CMS Landing Pages", href: "/app/admin/cms-landing-pages" },
    { icon: BarChart2, label: "Gestão de Campanhas", href: "/app/admin/campanhas" },
    { icon: Gift, label: "Concessões Manuais", href: "/app/admin/concessoes-manuais" },
    { icon: BarChart2, label: "Relatórios Gerais", href: "/app/admin/relatorios" },
    { icon: FileSearch, label: "Relatório de Usuário", href: "/app/admin/relatorio-usuario" },
    { icon: Shield, label: "Moderação de Conteúdo", href: "/app/admin/moderacao" },
  ]
};

export function AppSidebar({ collapsed, onToggle, breakpoint = 'desktop' }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const { toast } = useToast();
  const { role } = useUserProfile();
  const [professionalExpanded, setProfessionalExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  // Check if current path matches the item href
  const isActiveItem = (href: string) => location.pathname === href;
  
  // Check if any item in a group is active
  const hasActiveGroupItem = (group: MenuGroup) => 
    group.items.some(item => isActiveItem(item.href));

  // Auto-expand groups that contain the active item
  useEffect(() => {
    if (hasActiveGroupItem(professionalMenu)) {
      setProfessionalExpanded(true);
    }
    if (hasActiveGroupItem(adminMenu)) {
      setAdminExpanded(true);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        toast({
          title: "Erro",
          description: "Falha ao fazer logout",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sucesso",
          description: "Logout realizado com sucesso!",
        });
        navigate('/app/login');
      }
    } catch (error) {
      console.error('Erro no logout:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao fazer logout",
        variant: "destructive",
      });
    }
  };

  const MenuItemComponent = ({ item, isSubItem = false }: { item: MenuItem; isSubItem?: boolean }) => {
    const isActive = isActiveItem(item.href);
    
    if (item.disabled) {
      return (
        <div className={`
          flex flex-col p-3 rounded transition-all duration-200 relative group cursor-not-allowed
          text-sidebar-foreground/40
          ${isSubItem ? 'text-sm ml-6' : ''}
        `}>
          <div className="flex items-center">
            <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'} text-sidebar-foreground/40`} />
            {!collapsed && (
              <div className="flex flex-col">
                <span>{item.label}</span>
                <span className="text-xs text-sidebar-foreground/50 mt-1">em breve</span>
              </div>
            )}
          </div>
          {collapsed && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              {item.label} - em breve
            </div>
          )}
        </div>
      );
    }
    
    return (
      <Link
        to={item.href}
        className={`
          flex items-center p-3 rounded transition-all duration-200 relative group
          ${isActive 
            ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-l-4 border-l-yellow-500 font-medium' 
            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
          }
          ${isSubItem ? 'text-sm ml-6' : ''}
        `}
      >
        <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'} ${isActive ? 'text-yellow-600 dark:text-yellow-400' : ''}`} />
        {!collapsed && <span>{item.label}</span>}
        {item.badge && !collapsed && (
          <span className="ml-auto bg-sidebar-primary text-sidebar-primary-foreground text-xs px-2 py-1 rounded-full">
            {item.badge}
          </span>
        )}
        {collapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
            {item.label}
          </div>
        )}
      </Link>
    );
  };

  const MenuGroupComponent = ({ group, expanded, onToggle }: { 
    group: MenuGroup; 
    expanded: boolean; 
    onToggle: () => void; 
  }) => (
    <div className="mt-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 rounded text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200 group"
      >
        <div className="flex items-center">
          <group.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'}`} />
          {!collapsed && <span className="font-medium">{group.label}</span>}
        </div>
        {!collapsed && (
          expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
        {collapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
            {group.label}
          </div>
        )}
      </button>
      {expanded && !collapsed && (
        <div className="ml-6 mt-2 space-y-1">
          {group.items.map((item, index) => (
            <MenuItemComponent key={index} item={item} isSubItem />
          ))}
        </div>
      )}
    </div>
  );

  const getSidebarWidth = () => {
    if (collapsed) return 'w-20';
    // Reduce width on tablets for better responsiveness
    return breakpoint === 'tablet' ? 'w-60' : 'w-72';
  };

  const showToggleButton = true;

  return (
    <aside className={`
      bg-sidebar text-sidebar-foreground shadow-lg flex flex-col h-full transition-all duration-300 z-20
      ${getSidebarWidth()}
    `}>
      {/* Logo and Toggle */}
      <div className="flex-shrink-0">
        <div className="p-4 border-b border-sidebar-border flex items-center justify-between h-16">
          <img 
            src="/lovable-uploads/39c21a26-18ce-4bd9-b27c-9b4e2671dade.png" 
            alt="AURACODE Logo" 
            width="600"
            height="200"
            className={`
              object-contain transition-all duration-300
              ${collapsed 
                ? 'h-8 w-8' 
                : 'h-10 w-auto max-w-[150px]'
              }
            `}
            loading="lazy"
            decoding="async"
          />
          {showToggleButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
            >
              <Menu className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-2 py-4">
        <nav>
          {/* Main Menu */}
          <ul className="space-y-1">
            {mainMenuItems.map((item, index) => (
              <li key={index}>
                <MenuItemComponent item={item} />
              </li>
            ))}
          </ul>

          {/* Professional Menu - Only visible for Professional and Super Admin */}
          {(role === 'profissional' || role === 'super_administrador') && (
            <div className="mt-6 pt-4 border-t border-sidebar-border">
              <MenuGroupComponent 
                group={professionalMenu}
                expanded={professionalExpanded}
                onToggle={() => setProfessionalExpanded(!professionalExpanded)}
              />
            </div>
          )}

          {/* Admin Menu - Only visible for Super Admin */}
          {role === 'super_administrador' && (
            <MenuGroupComponent 
              group={adminMenu}
              expanded={adminExpanded}
              onToggle={() => setAdminExpanded(!adminExpanded)}
            />
          )}
        </nav>
      </div>

      {/* Utilities - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-sidebar-border">
        <ul className="space-y-2">
          <li>
            <Link
              to="/app/perfil"
              className={`
                flex items-center p-3 rounded transition-all duration-200 group
                ${isActiveItem("/app/perfil") 
                  ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-l-4 border-l-yellow-500 font-medium' 
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
                }
              `}
            >
              <Settings className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'} ${isActiveItem("/app/perfil") ? 'text-yellow-600 dark:text-yellow-400' : ''}`} />
              {!collapsed && <span>Perfil e Configurações</span>}
              {collapsed && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Perfil e Configurações
                </div>
              )}
            </Link>
          </li>
          <li>
            <ThemeToggle collapsed={collapsed} />
          </li>
          <li>
            <Link
              to="/app/suporte"
              className="flex items-center p-3 rounded text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200 group"
            >
              <HelpCircle className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'}`} />
              {!collapsed && <span>Suporte & Ajuda</span>}
              {collapsed && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Suporte & Ajuda
                </div>
              )}
            </Link>
          </li>
          <li>
            <button
              onClick={handleLogout}
              className="w-full flex items-center p-3 rounded text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200 group"
            >
              <LogOut className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-3'}`} />
              {!collapsed && <span>Sair</span>}
              {collapsed && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Sair
                </div>
              )}
            </button>
          </li>
        </ul>
      </div>
    </aside>
  );
}